import axios from 'axios';
import { apiURLPrint } from 'global_config/constant-global';
import moment from 'moment';
import { logAuditTrailEvent } from 'services/system_setup/auditTrail';

const MODULE_CODE = 'pymt';

function logPaymentAction(action, description, payload, isSuccess = 1) {
	void logAuditTrailEvent({
		moduleCode: MODULE_CODE,
		subModuleCode: 'payments',
		action,
		description,
		payload,
		isSuccess,
	});
}

function normalizePaymentTransactionAction(header = {}) {
	const statusName = String((header && header.status_name) || '').trim().toUpperCase();

	if (statusName === 'POST' || statusName === 'POSTED') return 'POST';
	if (statusName === 'CANCEL' || statusName === 'CANCELLED') return 'CANCEL';
	return 'SAVE';
}

function buildPaymentAuditSnapshot(header = {}, detail = []) {
	const statusName = String((header && header.status_name) || '').trim().toUpperCase();
	let targetStatus = 'NEW';

	if (statusName === 'POST' || statusName === 'POSTED') targetStatus = 'POSTED';
	if (statusName === 'CANCEL' || statusName === 'CANCELLED') targetStatus = 'CANCELLED';

	return {
		transaction_type: 'PAYMENT',
		transaction_no: (header && (header.or_no || header.receipt_no)) || '',
		payment_id: (header && header.payment_id) || null,
		receipt_no: (header && (header.or_no || header.receipt_no)) || '',
		previous_status: (header && header.status_name) || '',
		target_status: targetStatus,
		customer_id: (header && header.customer_id) || null,
		invoice_id: (header && header.invoice_id) || null,
		total_payment_amount: (header && header.total_payment_amount) || '',
		total_amount_due: (header && header.total_amount_due) || '',
		detail_count: Array.isArray(detail) ? detail.length : 0,
	};
}

function parseCurrencyValue(value) {
	if (value === null || value === undefined) return NaN;
	const sanitized = value.toString().replace(/,/g, '').trim();
	if (sanitized === '') return NaN;
	const parsed = parseFloat(sanitized);
	return Number.isFinite(parsed) ? parsed : NaN;
}

function formatCurrencyValue(value) {
	if (!Number.isFinite(value)) return '0.00';
	return value.toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function normalizeInvoiceHistoryPayload(data) {
	const source = Array.isArray(data) ? (data[0] || {}) : (data || {});
	const payload = source.json_results && typeof source.json_results === 'object'
		? source.json_results
		: source;
	const details = Array.isArray(payload.details) ? payload.details : [];

	const normalizedDetails = details.map(detail => ({
		...detail,
		payment_amount: Number.isFinite(parseCurrencyValue(detail.payment_amount))
			? formatCurrencyValue(parseCurrencyValue(detail.payment_amount))
			: detail.payment_amount,
	}));

	const totalPaid = normalizedDetails.reduce((sum, detail) => {
		const payment = parseCurrencyValue(detail.payment_amount);
		return sum + (Number.isFinite(payment) ? payment : 0);
	}, 0);

	const totalDue = parseCurrencyValue(payload.total_amount_due);
	const parsedInvoiceAmount = parseCurrencyValue(payload.invoice_amount);
	const invoiceAmount = Number.isFinite(parsedInvoiceAmount)
		? parsedInvoiceAmount
		: totalDue;
	let balance = parseCurrencyValue(payload.balance);

	if (!Number.isFinite(balance) && Number.isFinite(invoiceAmount)) {
		balance = invoiceAmount - totalPaid;
	}
	if (Number.isFinite(balance) && balance < 0) {
		balance = 0;
	}

	return {
		...payload,
		invoice_amount: Number.isFinite(invoiceAmount)
			? formatCurrencyValue(invoiceAmount)
			: payload.invoice_amount,
		balance: Number.isFinite(balance)
			? formatCurrencyValue(balance)
			: payload.balance,
		details: normalizedDetails,
	};
}

export async function searchPayments(payload){
	let payments = [];	
	const receipt_no = payload.receipt_no === undefined ? '' : payload.receipt_no;
	const payment_start_date = (payload.payment_date === undefined || payload.payment_date === null || !Array.isArray(payload.payment_date) || payload.payment_date.length === 0) 
		? '' : moment(payload.payment_date[0]).format('MM-DD-YYYY');
	const payment_end_date = (payload.payment_date === undefined || payload.payment_date === null || !Array.isArray(payload.payment_date) || payload.payment_date.length === 0) 
		? '' : moment(payload.payment_date[1]).format('MM-DD-YYYY');
	const invoice_no = payload.invoice_no === undefined ? '' : payload.invoice_no;  
	const customer = payload.customer === undefined ? '' : payload.customer;

	try {

		
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}payments/search?receipt_no=${receipt_no}&payment_start_date=${payment_start_date}&payment_end_date=${payment_end_date}&invoice_no=${invoice_no}&customer=${customer}`,
			headers: {
				'content-type':'application/json',
			},
		});

		const { data } = response;
		payments = data;
	}catch(error){
		logPaymentAction('SEARCH', 'Payment search failed', { request: payload, error: error && error.message }, 0);
		throw error;
	}
	logPaymentAction('SEARCH', 'Payment search', { request: payload, resultCount: payments.length }, 1);
	return payments;
}

export async function fetchInvoiceHistory(invoiceNo){
	try{
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}payments/payHist/${invoiceNo}`,
			headers: {
				'content-type': 'application/json',
			}
		});

		const { data } = response;
		const normalized = normalizeInvoiceHistoryPayload(data);
		logPaymentAction('VIEW', 'View payment invoice history', { request: { invoiceNo } }, 1);
		return normalized;
	}catch(error){
		logPaymentAction('VIEW', 'View payment invoice history failed', { request: { invoiceNo }, error: error && error.message }, 0);
		throw error;
	}	
}

export async function fetchPaymentMethod() {
	let paymentmethodlist = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}lov/paymentmethodlist/`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		paymentmethodlist = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return paymentmethodlist;
}

export async function fetchBank() {
	let banklist = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}lov/banklist/`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		banklist = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return banklist;
}

export async function fetchVatInput() {
	let vatlist = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}lov/vatinputlist/`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		vatlist = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return vatlist;
}

/*Add Payment Header*/
export async function addPaymentHeader(header,detail) {
	let paymentResponse = null;
	const action = normalizePaymentTransactionAction(header);
	const snapshot = buildPaymentAuditSnapshot(header, detail);
	const transactionLabel = snapshot.receipt_no ? ` ${snapshot.receipt_no}` : '';

	try {
		const response = await axios({
			method: 'POST',
			url: `${process.env.REACT_APP_BACKEND_API}payments/addpaymentheader`,
			data: {header,detail},
		}).then(response => {
			return response;
		});

		paymentResponse = response;
	} catch (error) {
		logPaymentAction(action, `${action} payment${transactionLabel} failed`, { ...snapshot, error: error && error.message }, 0);
		throw error;
		// Message.error();
	}

	logPaymentAction(action, `${action} payment${transactionLabel}`, snapshot, 1);
	return paymentResponse;
}

//update payments header
export async function updatePaymentHeader(header,detail) {
	
	let paymentData = null;
	const action = normalizePaymentTransactionAction(header);
	const snapshot = buildPaymentAuditSnapshot(header, detail);
	const transactionLabel = snapshot.receipt_no ? ` ${snapshot.receipt_no}` : '';
	try{
		const axiosResponse = await axios({
			method: 'PUT',
			url: `${process.env.REACT_APP_BACKEND_API}payments/updatePaymentHeader/`,
			data: {header, detail}
		}).then(response => {
			return response;
		});
  
		// @ts-ignore
		paymentData = axiosResponse;
	} 
	catch(error) {
		logPaymentAction(action, `${action} payment${transactionLabel} failed`, { ...snapshot, error: error && error.message }, 0);
		throw error;
		// Message.error();
	}
	
	logPaymentAction(action, `${action} payment${transactionLabel}`, snapshot, 1);
	return paymentData;
}

export async function fetchCustomersName() {
	let customersNamelist = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}payments/customerLov/`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		customersNamelist = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return customersNamelist;
}

// get invoice payments balance
export async function fetchBalanceDetailsAPI(customerID) {
	let balancedetails = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}payments/balancedetails/${customerID}`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		balancedetails = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return balancedetails;
}

// get invoice payments balance for update/edit NEW-status scenario
export async function fetchBalanceDetailsNewStatusAPI(customerID, paymentID) {
	let balancedetails = [];

	try {
		const paymentQuery = paymentID !== undefined && paymentID !== null && paymentID !== ''
			? `?payment_id=${paymentID}`
			: '';

		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}payments/fetchbalancenewstatus/${customerID}${paymentQuery}`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		balancedetails = data;
	} catch (error) {
		throw error;
	}
	return balancedetails;
}

export async function fetchEwt() {
	let ewtlist = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}lov/ewt/`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		ewtlist = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return ewtlist;
}

export async function fetchPaymentDetails(payment_id) {
	let details = [];

	try {
		const response = await axios({
			method: 'POST',
			url: `${process.env.REACT_APP_BACKEND_API}payments/paymentdetails/`,
			data: payment_id,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		details = data;
	} catch (error) {
		logPaymentAction('VIEW', 'View payment details failed', { request: payment_id, error: error && error.message }, 0);
		throw error;
		// Message.error();
	}
	logPaymentAction('VIEW', 'View payment details', { request: payment_id }, 1);
	return details;
}

export async function fetchHeaderDetails(or_no) {
	// console.log("🚀 ~ file: payments.jsx ~ line 245 ~ fetchHeaderDetails ~ or_no", or_no)
	let details = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}payments/refreshHeader/${or_no}`,
			data: or_no,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		details = data;
		// console.log("🚀 ~ file: payments.jsx ~ line 259 ~ fetchHeaderDetails ~ details", details)
	} catch (error) {
		throw error;
		// Message.error();
	}
	return details;
}

export async function fetchStatus() {
	let statuslist = [];

	try {
		const response = await axios({
			method: 'GET',
			url: `${process.env.REACT_APP_BACKEND_API}lov/statuslist/`,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		statuslist = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return statuslist;
}

export async function fetchBilling(CustId) {
	let details = [];

	try {
		const response = await axios({
			method: 'POST',
			url: `${process.env.REACT_APP_BACKEND_API}payments/getbillingadd/`,
			data: CustId,
			headers: {
				'content-type': 'application/json',
			},
		});

		const { data } = response;
		details = data;
	} catch (error) {
		throw error;
		// Message.error();
	}
	return details;
}

//customers details
export async function fetchCustomerDetailsAPI(customer_id) {
	let customersdetails = null;
	
	try{
		const response = await axios({
			method: 'POST',
			url: `${process.env.REACT_APP_BACKEND_API}invoice/customersdetails`,
			data: { customer_id },
			headers: { 
				'content-type': 'application/json',
			}
		});

		const { data } = response;
		customersdetails = data;
	}
	catch(error) {
		throw error;
		// Message.error();
	}

	return customersdetails;
}

export async function deletePaymentLines(deletedLines){
	try {
		const response = await axios({
			method: 'DELETE',
			url : `${process.env.REACT_APP_BACKEND_API}payments/delpaydetail`,
			data: deletedLines,
			headers:{
				'content-type': 'application/json',
			}
		});

		const { data } = response;
		logPaymentAction('DELETE', 'Delete payment lines', { request: deletedLines }, 1);
		return data;
		
	} catch (error) {
		logPaymentAction('DELETE', 'Delete payment lines failed', { request: deletedLines, error: error && error.message }, 0);
	}
}

//customers details
export async function fetchExistingOr(or_no) {
	let details = null;
	
	try{
		const response = await axios({
			method: 'POST',
			url: `${process.env.REACT_APP_BACKEND_API}payments/getexistingor`,
			data: { or_no },
			headers: { 
				'content-type': 'application/json',
			}
		});

		const { data } = response;
		details = data;
	}
	catch(error) {
		throw error;
		// Message.error();
	}
	return details;
}


export async function generatePaymentsReport (payload){
	let details = null;
	
	try {
		const response = await axios({
			method: 'POST',
			// url: `${apiURLPrint}grp_reports/generate.php`,
			//url: `https://grp.dxvista.com/reports/jrprintform/grp/payment/PRT_PAYMENTS.jrxml`,
			url: `${process.env.REACT_APP_REPORTS_API}payment/PRT_PAYMENTS.jrxml`,
			data: JSON.stringify( payload ),
			headers: { 
			 	'Content-Type': 'application/json',
				'Access-Control-Allow-Origin':'*'
			}
		});
			

		const { data } = response;
		details = data;
	} catch (error) {
		throw error;
	}
	return details;
}

//added feature for deleted lines in updating payments
export async function tmpDeleteDetail(paymentLineID) {
	
	let paymentData = null;
	try{
		const axiosResponse = await axios({
			method: 'PUT',
			url: `${process.env.REACT_APP_BACKEND_API}payments/tmpDeleteDetail/${paymentLineID}`,
			// data: paymentLineID,
			// headers: {
			// 	'content-type': 'application/json',
			// },
		}).then(response => {
			return response;
		});
  
		// @ts-ignore
		paymentData = axiosResponse;
	} 
	catch(error) {
		throw error;
		// Message.error();
	}
	
	return paymentData;
}
